/**
 * One workspace view adapter (Part 06 decision 2): what the rest of the app
 * asks of the workspace view without knowing whether it is the wall or the
 * old canvas. `workspaceViewAdapter()` follows `preferred`, always the wall
 * outside tests; a long operation captures its adapter at the start. Every
 * wall method that reads the screen answers null/false unless the wall shows
 * in this window.
 */
import { wallViewAdapter } from "@/components/wall/wall-view-adapter";
import { canvasViewAdapter } from "@/components/canvas/canvas-view-adapter";
import type { FrameType } from "@/api/spaces";
import type { FrameScreenRect } from "@/components/cursors/frame-cursor-geometry";
import { isWallActive } from "@/stores/workspace-view-store";

/** Workspace point = client point × zoom + offset; the wall is the identity. */
export interface WorkspaceTransform {
  x: number;
  y: number;
  zoom: number;
}

export interface WorkspacePoint {
  x: number;
  y: number;
}

export interface FrameSize {
  width: number;
  height: number;
}

/** Where new frames go: the canvas places them, the wall leaves it to the backend. */
export type FramePlacementRequest =
  /** New frames of `frameType` where Tidy would put the newest ones. */
  | { kind: "tidy"; frameType: FrameType; sizes: readonly FrameSize[] }
  /** One frame to the right of `sourceFrameId`. */
  | { kind: "beside"; sourceFrameId: string | undefined }
  /** New frames in a row from `near` (default: the view's centre), past the others. */
  | { kind: "near"; near?: WorkspacePoint; sizes: readonly FrameSize[] };

export interface WorkspaceViewAdapter {
  kind: "wall" | "canvas";
  /** A frame's card or node in client pixels, or null when not shown here. */
  frameRect: (frameId: string) => FrameScreenRect | null;
  /** Fires when the rects `frameRect` returns may have changed. */
  subscribe: (listener: () => void) => () => void;
  /** Bring a frame's card into view; resolves once it is settled there. */
  bringIntoView: (frameId: string, signal?: AbortSignal) => Promise<boolean>;
  /**
   * Show the workspace with `frameIds` settled in it (onboarding's last
   * scene): the wall at its top with every card and tile the frames get, the
   * canvas fitted to them. Rejects with `signal.reason` when aborted; the
   * caller owns the deadline.
   */
  settleFrames: (
    frameIds: readonly string[],
    signal: AbortSignal,
  ) => Promise<void>;
  /**
   * The whole workspace at rest: the wall's top, the canvas tidied.
   * Resolves once it shows.
   */
  showOverview: () => Promise<void>;
  /** A frame is about to arrive that a later `settleFrames` shows. */
  prepareFrameArrival: (frameId: string) => void;
  /** Workspace → client mapping for anchors kept in workspace points. */
  transform: () => WorkspaceTransform;
  /** `transform()`, re-rendering when it changes. */
  useTransform: () => WorkspaceTransform;
  /** A client point as a workspace point (the summon anchors). */
  toWorkspacePoint: (client: WorkspacePoint) => WorkspacePoint;
  /** One position per requested frame; undefined: send none, the backend places them. */
  framePositions: (
    request: FramePlacementRequest,
  ) => WorkspacePoint[] | undefined;
  /**
   * Show frames the user asked to see: an open focus adds them in place, the
   * wall opens them in focus, the canvas brings them into the camera.
   */
  revealFrames: (frameIds: readonly string[]) => void;
  /** Where new frames should land, sent with a prompt; undefined: let the backend place. */
  viewportCenterHint: () => WorkspacePoint | undefined;
  /** The ids among `conversationIds` whose frames show in this window. */
  visibleConversationIds: (conversationIds: readonly string[]) => string[];
}

export function workspaceViewAdapter(): WorkspaceViewAdapter {
  return isWallActive() ? wallViewAdapter : canvasViewAdapter;
}

/** Watch both views' rects for the life of a window. */
export function subscribeWorkspaceViews(listener: () => void): () => void {
  const unsubscribeWall = wallViewAdapter.subscribe(listener);
  const unsubscribeCanvas = canvasViewAdapter.subscribe(listener);
  return () => {
    unsubscribeWall();
    unsubscribeCanvas();
  };
}
